import { useId } from '@reach/auto-id';
import styled, { css, useTheme } from 'styled-components';
import React, { useRef, useState } from 'react';
import * as icons from '@boxine/tonies-ui/icons';
import { Paragraph } from '../../../atoms/Typography';
import { Box } from '../../../atoms/Box';
import { FocusStyles } from '../../../../styles/FocusStyles';
import Icon from '../../../../components/Icon';
import { useUniversalLayoutEffect } from '../../../../hooks/useUniversalLayoutEffect';
import { useAccordion } from './AccordionProvider';
// necessary to reference the wrapper in "Details"
export const Wrapper = styled(Box) ``;
const Details = styled.div `
  ${({ theme: { colors, colorContext } }) => css `
    border-bottom: 0.0625rem solid;
    border-bottom-color: ${colors[colorContext === 'dark' ? 'lightergrey' : 'middlegrey']};
  `};

  ${Wrapper} &:last-child {
    border-bottom: none;
  }
`;
const Summary = styled.button `
  cursor: pointer;
  border: none;
  background: transparent;
  position: relative;
  z-index: 10;
  padding: 1rem 0;
  list-style-type: none; /* Firefox */
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;

  ${FocusStyles};
`;
const IconWrapper = styled.span `
  margin-left: 1rem;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: relative;
  width: 1.5rem;
  height: 1.5rem;
  overflow: hidden;
`;
const StyledIcon = styled(Icon) `
  height: 1.5rem;
  width: 1.5rem;
  transition: all 0.3s;
  position: absolute;
  top: 0;
`;
const ArrowUp = styled(StyledIcon) `
  transform: translate3d(0, ${props => (props.isOpen ? 0 : '200%')}, 0);
`;
const ArrowDown = styled(StyledIcon) `
  transform: translate3d(0, ${props => (props.isOpen ? '200%' : '0')}, 0);
`;
const Content = styled.div `
  &[aria-hidden='true'] {
    display: none;
  }

  &[aria-hidden='false'] {
    padding-bottom: 1rem;
  }
`;
export const AccordionItem = ({ className, title, 'data-trackingid': dataTrackingId, 'data-testid': dataTestId, children = null, }) => {
    const { colorContext } = useTheme();
    const { selectedId, setSelectedId } = useAccordion();
    const idContent = useId() || '';
    const idHeader = useId() || '';
    const isOpen = Boolean(selectedId && idHeader && selectedId === idHeader);
    const ref = useRef(null);
    // Work around an issue in Chrome 87+ which doesn't update the scroll
    // value when clicking an accordion button. When a large item is open
    // and the user clicks another item further down the page will be stuck
    // in the previous position. This bug is not present in Chrome 86.
    const [top, setTop] = useState(0);
    useUniversalLayoutEffect(() => {
        if (ref.current && top > 0 && isOpen) {
            const rect = ref.current.getBoundingClientRect();
            const diff = top - rect.top;
            if (diff !== 0) {
                window.scrollTo({
                    behavior: 'auto',
                    top: window.scrollY - diff,
                });
            }
            setTop(0);
        }
    }, [isOpen, top]);
    return (React.createElement(Details, { className: className, ref: ref },
        React.createElement(Summary, { type: "button", id: idHeader, "aria-controls": idContent, "aria-expanded": isOpen, "data-trackingid": dataTrackingId && `${dataTrackingId}${isOpen ? 'fold' : 'unfold'}`, "data-testid": dataTestId, onClick: e => {
                e.preventDefault();
                setSelectedId(isOpen ? '' : idHeader);
                if (ref.current) {
                    const rect = ref.current.getBoundingClientRect();
                    isOpen ? setTop(rect.top) : setTop(0);
                }
            } },
            React.createElement(Paragraph, { asHTMLTag: "span", color: colorContext === 'dark' ? 'white' : 'darkergrey', weight: isOpen ? 700 : 500, align: "left" }, title),
            React.createElement(IconWrapper, null,
                React.createElement(ArrowUp, { fill: colorContext === 'dark' ? 'white' : 'darkergrey', type: icons.arrowUp, isOpen: isOpen }),
                React.createElement(ArrowDown, { fill: colorContext === 'dark' ? 'white' : 'darkergrey', type: icons.arrowDown, isOpen: isOpen }))),
        React.createElement(Content, { role: "region", "aria-labelledby": idHeader, "aria-hidden": !isOpen, id: idContent, "data-testid": dataTestId + '__content' },
            React.createElement(Paragraph, { asHTMLTag: "div", size: 2, color: colorContext === 'dark' ? 'white' : 'darkergrey' }, children))));
};
